<script setup lang="ts">
import type { NuxtError } from '#app'
import { useHomeQuery } from '~~/app/composables/queries/useStoreApi'
import { ALL_CATEGORIES_HREF } from '~~/shared/utils/allCategoriesLink'

const props = defineProps<{
  error: NuxtError
}>()

const brand = useBrand()
const router = useRouter()

// Przy błędzie NuxtRoot renderuje ten komponent ZAMIAST app.vue (v-else-if),
// więc useBrandHead() z app.vue tu nie działa — bez tego strona błędu nie
// miała <html data-brand> (motyw lavmaga na każdej marce), titleTemplate ani
// favikony marki. Duplikatów nie ma: app.vue i error.vue nigdy nie są
// zamontowane naraz, także przy showError po nawigacji.
useBrandHead()

// Sklep katalogowy (proles) to upominki bez sprzedaży — teksty o outlecie,
// dostawach marek i tym, co „kupują klientki", nie mają tam pokrycia.
const { enabled: catalogOnly } = useCatalogOnly()

const statusCode = computed(() => Number(props.error?.statusCode) || 500)
const isNotFound = computed(() => statusCode.value === 404)

const searchInput = ref('')
// Przykłady z asortymentu marki — katalog upominków nie ma sukienek.
const searchPlaceholder = `Czego szukasz? Np. ${brand.search?.examples ?? 'sukienka, kurtka, buty'}…`

async function submitSearch() {
  const q = searchInput.value.trim()
  await clearError({
    redirect: q ? `/search?q=${encodeURIComponent(q)}` : '/search',
  })
}

const { data: homeData } = useHomeQuery()
const recommendedProducts = computed(() => {
  if (!isNotFound.value) return []
  return (homeData.value?.trending ?? []).slice(0, 12)
})

const heading = computed(() =>
  isNotFound.value ? 'Tej strony już tu nie ma.' : 'Coś poszło nie tak.',
)

const lead = computed(() =>
  isNotFound.value
    ? 'Adres może być nieaktualny, a strona została przeniesiona lub usunięta. Wybierz jeden z poniższych kierunków — pomożemy Ci wrócić na właściwy szlak.'
    : 'Po naszej stronie wystąpił nieoczekiwany błąd. Odśwież stronę za chwilę albo wróć na stronę główną — postaramy się to jak najszybciej naprawić.',
)

const label = computed(() => `Błąd ${statusCode.value}`)

const seoTitle = computed(() =>
  isNotFound.value
    ? 'Strona nie istnieje'
    : 'Wystąpił błąd',
)
const seoDescription = computed(() =>
  isNotFound.value
    ? `Nie znaleźliśmy tej strony. Wróć na stronę główną ${brand.name} lub przejdź do kategorii.`
    : `Wystąpił błąd na stronie ${brand.name}. Spróbuj ponownie za chwilę.`,
)

useSeoMeta({
  title: seoTitle,
  description: seoDescription,
  ogTitle: seoTitle,
  ogDescription: seoDescription,
  ogType: 'website',
  robots: 'noindex, follow',
})

function goHome() {
  clearError({ redirect: '/' })
}

function goBack() {
  if (import.meta.client && window.history.length > 1) {
    router.back()
  } else {
    clearError({ redirect: '/' })
  }
}

const popularDestinations = [
  {
    num: '01',
    href: '/',
    label: 'Strona główna',
    desc: `Wróć do startu — pełna oferta ${brand.name} czeka.`,
  },
  {
    num: '02',
    href: ALL_CATEGORIES_HREF,
    label: 'Wszystkie produkty',
    desc: catalogOnly
      ? 'Przeglądaj cały katalog upominków firmowych.'
      : 'Przeglądaj cały asortyment naszego outletu.',
  },
  {
    num: '03',
    href: '/nowosci',
    label: 'Nowości',
    desc: catalogOnly
      ? 'Zobacz produkty, które niedawno dołączyły do katalogu.'
      : 'Sprawdź najnowsze dostawy z topowych marek.',
  },
  {
    num: '04',
    href: '/contact',
    label: 'Kontakt',
    desc: 'Nie znalazłeś czego szukasz? Napisz do nas.',
  },
] as const
</script>

<template>
  <NuxtLayout>
    <div class="error-page">
      <!-- ─── HERO ─── -->
      <section class="hero">
        <div class="hero__inner container">
          <p class="hero__label">{{ label }}</p>
          <p class="hero__code" aria-hidden="true">{{ statusCode }}</p>
          <h1 class="hero__heading">{{ heading }}</h1>
          <p class="hero__lead">{{ lead }}</p>
          <div class="hero__actions">
            <button
              type="button"
              class="btn btn-primary hero__cta"
              @click="goHome"
            >
              Wróć do strony głównej
            </button>
            <button
              type="button"
              class="btn btn-secondary hero__cta"
              @click="goBack"
            >
              Cofnij się
            </button>
          </div>

          <form
            v-if="isNotFound"
            class="hero__search"
            role="search"
            @submit.prevent="submitSearch"
          >
            <label for="error-search" class="hero__search-label">
              Albo poszukaj czegoś konkretnego
            </label>
            <div class="hero__search-control">
              <input
                id="error-search"
                v-model="searchInput"
                class="hero__search-input"
                type="search"
                :placeholder="searchPlaceholder"
                autocomplete="off"
              >
              <button
                type="submit"
                class="hero__search-btn"
                aria-label="Szukaj"
              >
                <svg
                  width="20"
                  height="20"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="1.5"
                  aria-hidden="true"
                >
                  <circle cx="11" cy="11" r="7" />
                  <path d="m20 20-3.5-3.5" stroke-linecap="round" />
                </svg>
              </button>
            </div>
          </form>
        </div>
      </section>

      <!-- ─── SUGGESTIONS ─── -->
      <section v-if="isNotFound" class="suggestions">
        <div class="suggestions__inner container">
          <p class="suggestions__label">Popularne kierunki</p>
          <h2 class="suggestions__heading">Może to Cię zainteresuje</h2>
          <div class="suggestions__grid">
            <NuxtLink
              v-for="dest in popularDestinations"
              :key="dest.href"
              :to="dest.href"
              class="suggestions__card"
              @click="() => clearError()"
            >
              <span class="suggestions__num">{{ dest.num }}</span>
              <span class="suggestions__title">{{ dest.label }}</span>
              <span class="suggestions__desc muted">{{ dest.desc }}</span>
              <span class="suggestions__arrow" aria-hidden="true">
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 16 16"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="1.5"
                >
                  <path d="M3 8h10M9 4l4 4-4 4" stroke-linecap="round" />
                </svg>
              </span>
            </NuxtLink>
          </div>
        </div>
      </section>

      <!-- ─── EDITORIAL CAROUSEL ─── -->
      <section
        v-if="isNotFound && recommendedProducts.length"
        class="editorial"
      >
        <div class="editorial__head container">
          <p class="editorial__label">Inspiracje</p>
          <template v-if="catalogOnly">
            <h2 class="editorial__heading">Wybrane z katalogu</h2>
            <p class="editorial__lead muted">
              Może znajdziesz tu coś dla swojej marki. Każdy produkt możesz dodać
              do listy zapytania i poprosić o wycenę.
            </p>
          </template>
          <template v-else>
            <h2 class="editorial__heading">Sprawdź, co teraz pokochali inni</h2>
            <p class="editorial__lead muted">
              Może znajdziesz tu coś dla siebie. Bestsellery {{ brand.name }} —
              produkty, które kupują nasze klientki i klienci.
            </p>
          </template>
        </div>
        <ProductScrollSnapCarousel
          :products="recommendedProducts"
          item-list-id="error_404_trending"
          item-list-name="404 · Bestsellery"
        />
      </section>
    </div>
  </NuxtLayout>
</template>

<style scoped>
.error-page {
  background: var(--color-white);
}

/* ─── HERO ─── */
.hero {
  background: var(--color-white);
  padding: clamp(72px, 10vw, 144px) 0 clamp(56px, 7vw, 112px);
  border-bottom: 1px solid var(--color-border);
  position: relative;
  overflow: hidden;
}

.hero__inner {
  display: grid;
  gap: 24px;
  max-width: 920px;
  position: relative;
  z-index: 1;
}

.hero__label {
  margin: 0;
  font-size: 0.75rem;
  font-weight: var(--font-weight-bold);
  letter-spacing: 0.22em;
  text-transform: uppercase;
  color: var(--color-muted);
}

.hero__code {
  margin: 8px 0 0;
  font-size: clamp(7rem, 22vw, 18rem);
  font-weight: var(--font-weight-light);
  letter-spacing: -0.04em;
  line-height: 0.85;
  color: var(--color-black);
  user-select: none;
}

.hero__heading {
  margin: 0;
  font-size: clamp(2rem, 4.5vw, 3.5rem);
  font-weight: var(--font-weight-light);
  letter-spacing: -0.025em;
  line-height: 1.05;
  color: var(--color-black);
  max-width: 700px;
}

.hero__lead {
  margin: 0;
  max-width: 620px;
  font-size: clamp(1rem, 1.1vw, 1.125rem);
  line-height: 1.7;
  color: var(--color-muted);
  font-weight: var(--font-weight-light);
}

.hero__actions {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  margin-top: 16px;
}

.hero__cta {
  min-width: 220px;
  padding: 16px 32px;
  font-size: 0.9375rem;
  letter-spacing: 0.02em;
}

/* ─── HERO SEARCH ─── */
.hero__search {
  display: grid;
  gap: 10px;
  margin-top: 32px;
  max-width: 560px;
}

.hero__search-label {
  font-size: 0.6875rem;
  font-weight: var(--font-weight-bold);
  letter-spacing: 0.22em;
  text-transform: uppercase;
  color: var(--color-muted);
}

.hero__search-control {
  position: relative;
  display: flex;
  align-items: center;
  background: var(--color-white);
  border: 1px solid var(--color-border-md);
  border-radius: 999px;
  transition:
    border-color var(--transition-fast),
    box-shadow var(--transition-fast);
}

.hero__search-control:focus-within {
  border-color: var(--color-black);
  box-shadow: 0 0 0 3px rgba(21, 21, 21, 0.06);
}

.hero__search-input {
  flex: 1;
  min-width: 0;
  border: 0;
  background: transparent;
  padding: 18px 24px;
  font-family: var(--font-body);
  font-size: 0.9375rem;
  font-weight: var(--font-weight-light);
  color: var(--color-black);
  outline: none;
}

.hero__search-input::placeholder {
  color: var(--color-muted);
}

.hero__search-input::-webkit-search-cancel-button {
  appearance: none;
}

.hero__search-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 48px;
  height: 48px;
  margin: 4px;
  background: var(--color-black);
  color: var(--color-white);
  border: 0;
  border-radius: 50%;
  cursor: pointer;
  flex-shrink: 0;
  transition: background var(--transition-fast);
}

.hero__search-btn:hover {
  background: #2a2a2a;
}

/* ─── SUGGESTIONS ─── */
.suggestions {
  padding: clamp(64px, 8vw, 112px) 0;
  background: var(--color-bg);
}

.suggestions__inner {
  display: grid;
  gap: 12px;
}

.suggestions__label {
  margin: 0;
  font-size: 0.6875rem;
  font-weight: var(--font-weight-bold);
  letter-spacing: 0.22em;
  text-transform: uppercase;
  color: var(--color-muted);
}

.suggestions__heading {
  margin: 0 0 32px;
  font-size: clamp(1.75rem, 3vw, 2.5rem);
  font-weight: var(--font-weight-light);
  letter-spacing: -0.02em;
  line-height: 1.15;
  color: var(--color-black);
}

.suggestions__grid {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 1px;
  background: var(--color-border);
  border: 1px solid var(--color-border);
}

.suggestions__card {
  position: relative;
  display: grid;
  gap: 14px;
  align-content: start;
  padding: 28px 24px;
  background: var(--color-white);
  transition: background var(--transition-fast);
}

.suggestions__card:hover {
  background: var(--color-bg);
}

.suggestions__num {
  font-size: 0.75rem;
  font-weight: var(--font-weight-bold);
  letter-spacing: 0.18em;
  color: var(--color-muted);
}

.suggestions__title {
  font-size: 1.0625rem;
  font-weight: var(--font-weight-normal);
  color: var(--color-black);
  line-height: 1.3;
}

.suggestions__desc {
  font-size: 0.875rem;
  line-height: 1.55;
}

.suggestions__arrow {
  position: absolute;
  right: 20px;
  bottom: 20px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  color: var(--color-muted);
  opacity: 0;
  transform: translateX(-4px);
  transition:
    opacity var(--transition-fast),
    transform var(--transition-fast),
    color var(--transition-fast);
}

.suggestions__card:hover .suggestions__arrow,
.suggestions__card:focus-visible .suggestions__arrow {
  opacity: 1;
  transform: translateX(0);
  color: var(--color-black);
}

/* ─── EDITORIAL CAROUSEL ─── */
.editorial {
  padding: clamp(64px, 8vw, 112px) 0 clamp(72px, 10vw, 128px);
  background: var(--color-white);
  display: flex;
  flex-direction: column;
  gap: 32px;
}

.editorial__head {
  display: grid;
  gap: 12px;
  max-width: 720px;
}

.editorial__label {
  margin: 0;
  font-size: 0.6875rem;
  font-weight: var(--font-weight-bold);
  letter-spacing: 0.22em;
  text-transform: uppercase;
  color: var(--color-muted);
}

.editorial__heading {
  margin: 0;
  font-size: clamp(1.75rem, 3vw, 2.5rem);
  font-weight: var(--font-weight-light);
  letter-spacing: -0.02em;
  line-height: 1.15;
  color: var(--color-black);
}

.editorial__lead {
  margin: 0;
  font-size: clamp(0.9375rem, 1vw, 1.0625rem);
  line-height: 1.7;
}

/* ─── Responsive ─── */
@media (max-width: 1100px) {
  .suggestions__grid {
    grid-template-columns: repeat(2, 1fr);
  }
}

@media (max-width: 720px) {
  .hero__cta {
    width: 100%;
    min-width: 0;
  }

  .hero__search {
    max-width: none;
  }

  .hero__search-input {
    padding: 14px 18px;
  }

  .suggestions__grid {
    grid-template-columns: 1fr;
  }
}
</style>
